import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Sparkles, ShieldAlert, Play, Pause, Zap, RefreshCw, CheckCircle2, XCircle,
  Clock, TrendingUp, TrendingDown, Activity, DollarSign, Newspaper, Radio,
  AlertTriangle, Settings2, Bot, Beaker,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PageHeader, Table, Td, StatusBadge } from "../../components/dashboard/Shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { EligibilityDebug, ExperimentalMode, DirectMode } from "./DevMode";

const MODES = [
  { value: "MANUAL", label: "Manual Only" },
  { value: "AI_SUGGEST", label: "AI Suggest" },
  { value: "FULL_AUTO", label: "Full Auto" },
];
const SESSIONS = [
  { value: "BOTH", label: "London + New York" },
  { value: "LONDON", label: "London Only" },
  { value: "NEW_YORK", label: "New York Only" },
];
const RISK_PRESETS = [
  { value: "CONSERVATIVE", label: "Conservative (0.5%)" },
  { value: "MODERATE", label: "Moderate (1.0%)" },
  { value: "AGGRESSIVE", label: "Aggressive (1.5%)" },
  { value: "CUSTOM", label: "Custom" },
];

const fmt = (n, d = 2) => (typeof n === "number" ? n.toFixed(d) : n ?? "—");
const money = (n) => (typeof n === "number" ? `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—");

// -------------------------------------------------
// Signal Card — pending state with countdown
// -------------------------------------------------
function Countdown({ expiresAt, onExpire }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const target = new Date(expiresAt).getTime();
    const tick = () => {
      const s = Math.max(0, Math.floor((target - Date.now()) / 1000));
      setLeft(s);
      if (s === 0 && onExpire) onExpire();
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt, onExpire]);
  const m = Math.floor(left / 60);
  const s = left % 60;
  return (
    <span className={`tabular text-xs ${left <= 30 ? "text-[#FF3B30]" : "text-[#00F0FF]"}`}>
      {m}:{s.toString().padStart(2, "0")}
    </span>
  );
}

function SignalCard({ sig, onApprove, onReject }) {
  const isBuy = sig.direction === "BUY";
  const isPending = sig.status === "pending";
  const [busy, setBusy] = useState(false);
  const tierColor = sig.tier === "A+" ? "text-[#00F0FF]" : sig.tier === "STANDARD" ? "text-amber-400" : "text-gray-500";
  return (
    <div data-testid={`ai-signal-card-${sig.signal_id}`} className="card-dark p-5 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${isBuy ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "bg-[#FF3B30]/10 text-[#FF3B30]"}`}>
            {isBuy ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-display text-white text-lg leading-none">XAUUSD · {sig.direction}</p>
              <span className={`text-[10px] uppercase tracking-wider ${tierColor}`}>{sig.tier}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">{sig.pattern} · {sig.session} · rr {fmt(sig.rr_expected, 1)}</p>
          </div>
        </div>
        <div className="text-right">
          <StatusBadge status={
            sig.status === "pending" ? "pending" :
            sig.status === "executed" || sig.status === "executing" ? "paid" :
            sig.status === "expired" || sig.status === "rejected" || sig.status === "cancelled" ? "overdue" : "pending"
          } />
          {isPending && (
            <div className="flex items-center gap-1 mt-1 justify-end">
              <Clock size={12} className="text-gray-500" />
              <Countdown expiresAt={sig.expires_at} />
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 text-center pt-3 border-t border-white/5">
        <div>
          <p className="label-xs">Entry</p>
          <p className="tabular text-white text-sm">${fmt(sig.entry)}</p>
        </div>
        <div>
          <p className="label-xs">SL</p>
          <p className="tabular text-[#FF3B30] text-sm">${fmt(sig.sl)}</p>
        </div>
        <div>
          <p className="label-xs">TP1</p>
          <p className="tabular text-[#00F0FF] text-sm">${fmt(sig.tp1)}</p>
        </div>
        <div>
          <p className="label-xs">TP2</p>
          <p className="tabular text-[#00F0FF] text-sm">${fmt(sig.tp2)}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 text-center pt-2">
        <div>
          <p className="label-xs">Final</p>
          <p className="tabular text-white text-xl font-bold">{sig.final_score}</p>
        </div>
        <div>
          <p className="label-xs">Rule / Claude</p>
          <p className="tabular text-gray-300 text-sm">{sig.rule_score} / {sig.claude_score}</p>
        </div>
        <div>
          <p className="label-xs">Risk</p>
          <p className="tabular text-white text-sm">{fmt(sig.risk_pct, 1)}%</p>
        </div>
      </div>

      {sig.claude_reasoning && (
        <div className="text-xs text-gray-400 border-t border-white/5 pt-3 leading-relaxed italic">
          &ldquo;{sig.claude_reasoning}&rdquo;
        </div>
      )}
      {(sig.claude_red_flags || []).length > 0 && (
        <div className="text-xs text-amber-400 flex items-start gap-2">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span>{sig.claude_red_flags.join(" · ")}</span>
        </div>
      )}

      <div className="text-[10px] text-gray-600">
        D1 {sig.trend?.d1} · H4 {sig.trend?.h4} · H1 {sig.trend?.h1} · ATR ${fmt(sig.m5_atr)} · Spread ${fmt(sig.spread_at_signal)}
        {sig.swept_liquidity && <span className="ml-1 text-[#00F0FF]">· Sweep+</span>}
      </div>

      {isPending && (
        <div className="grid grid-cols-2 gap-2 pt-2">
          <button
            data-testid={`ai-approve-${sig.signal_id}`}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/claude/signals/${sig.signal_id}/approve`);
                toast.success("Signal approved & executing");
                onApprove();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Approve failed");
              } finally { setBusy(false); }
            }}
            className="btn-primary py-2 text-xs flex items-center justify-center gap-2 disabled:opacity-40"
          >
            <CheckCircle2 size={14} /> Approve & Execute
          </button>
          <button
            data-testid={`ai-reject-${sig.signal_id}`}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/claude/signals/${sig.signal_id}/reject?reason=manager_rejected`);
                toast.success("Signal rejected");
                onReject();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Reject failed");
              } finally { setBusy(false); }
            }}
            className="btn-outline py-2 text-xs flex items-center justify-center gap-2 disabled:opacity-40"
          >
            <XCircle size={14} /> Reject
          </button>
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------
// Status panel
// -------------------------------------------------
function StatusPanel({ status }) {
  if (!status) return <div className="skeleton h-32" />;
  const s = status.settings || {};
  const isActive = s.system_status === "ACTIVE" && s.enabled;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <div className="card-dark p-4">
        <p className="label-xs">System</p>
        <p className={`text-lg font-display ${isActive ? "text-[#00F0FF]" : "text-gray-400"}`}>
          {s.enabled ? s.system_status : "DISABLED"}
        </p>
        {s.pause_reason && <p className="text-[10px] text-amber-400 mt-1">{s.pause_reason}</p>}
      </div>
      <div className="card-dark p-4">
        <p className="label-xs">Mode</p>
        <p className="text-white text-lg font-display">{s.mode || "—"}</p>
        <p className="text-[10px] text-gray-500 mt-1">Min confidence {s.min_confidence}</p>
      </div>
      <div className="card-dark p-4">
        <p className="label-xs">Today P&L</p>
        <p className={`text-lg font-display tabular ${status.daily_pct > 0 ? "text-[#00F0FF]" : status.daily_pct < 0 ? "text-[#FF3B30]" : "text-gray-400"}`}>
          {fmt(status.daily_pct, 2)}%
        </p>
        <p className="text-[10px] text-gray-500 mt-1">Week {fmt(status.weekly_pct, 2)}%</p>
      </div>
      <div className="card-dark p-4">
        <p className="label-xs">Signals Today</p>
        <p className="text-white text-lg font-display tabular">{status.today?.signals ?? 0}</p>
        <p className="text-[10px] text-gray-500 mt-1">Win {status.today?.win_rate ?? 0}%</p>
      </div>
      <div className="card-dark p-4">
        <p className="label-xs flex items-center gap-1"><Bot size={11} /> Claude</p>
        <p className={`text-sm ${status.claude?.available ? "text-[#00F0FF]" : "text-[#FF3B30]"}`}>
          {status.claude?.available ? "Online" : "Offline"}
        </p>
        <p className="text-[10px] text-gray-500 mt-1">Streak fails {status.claude?.consecutive_failures ?? 0}</p>
      </div>
      <div className="card-dark p-4">
        <p className="label-xs flex items-center gap-1"><DollarSign size={11} /> Claude cost</p>
        <p className="text-white tabular text-sm">${fmt(status.today?.claude_cost_usd, 4)}</p>
        <p className="text-[10px] text-gray-500 mt-1">{status.today?.claude_calls ?? 0} calls · {status.today?.avg_latency_ms ?? 0}ms</p>
      </div>
      <div className="card-dark p-4">
        <p className="label-xs flex items-center gap-1"><Newspaper size={11} /> News</p>
        <p className={`text-sm ${status.news?.stale_hard_limit_exceeded ? "text-[#FF3B30]" : "text-white"}`}>
          {status.news?.events_count ?? 0} USD-HIGH
        </p>
        <p className="text-[10px] text-gray-500 mt-1">
          {status.news?.fetched_at ? new Date(status.news.fetched_at).toLocaleTimeString() : "not fetched"}
        </p>
      </div>
      <div className="card-dark p-4">
        <p className="label-xs flex items-center gap-1"><Radio size={11} /> Eligible clients</p>
        <p className="text-white text-lg font-display tabular">{status.eligible_accounts ?? 0}</p>
        <p className="text-[10px] text-gray-500 mt-1">Scheduler gate: {status.scheduler?.can_generate_new_signal ? "OK" : "blocked"}</p>
      </div>
    </div>
  );
}

// -------------------------------------------------
// Settings panel
// -------------------------------------------------
function SettingsPanel({ settings, onSaved }) {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(settings || {}); }, [settings]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/claude/settings", patch);
      setForm(data);
      toast.success("Saved");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const field = (key) => form[key] ?? "";

  return (
    <div className="card-dark p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white font-display text-lg flex items-center gap-2"><Settings2 size={16} /> Claude Signals Settings</p>
          <p className="text-xs text-gray-500 mt-1">Changes save immediately. Master toggle controls whether the scheduler runs at all.</p>
        </div>
        <button
          data-testid="ai-master-toggle"
          disabled={saving}
          onClick={() => save({ enabled: !form.enabled })}
          className={`px-5 py-2 rounded-lg text-sm flex items-center gap-2 ${form.enabled ? "bg-[#00F0FF]/15 text-[#00F0FF] border border-[#00F0FF]/40" : "bg-white/5 border border-white/10 text-gray-300"}`}
        >
          {form.enabled ? <Play size={14} /> : <Pause size={14} />}
          {form.enabled ? "ON" : "OFF"}
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <label className="text-xs">
          <span className="label-xs block mb-1">Mode</span>
          <select data-testid="ai-mode-select" value={field("mode")} disabled={saving}
                  onChange={(e) => save({ mode: e.target.value })} className="input-dark">
            {MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="label-xs block mb-1">Session</span>
          <select data-testid="ai-session-select" value={field("session_pref")} disabled={saving}
                  onChange={(e) => save({ session_pref: e.target.value })} className="input-dark">
            {SESSIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
        <label className="text-xs">
          <span className="label-xs block mb-1">Risk Preset</span>
          <select data-testid="ai-risk-preset" value={field("risk_preset")} disabled={saving}
                  onChange={(e) => save({ risk_preset: e.target.value })} className="input-dark">
            {RISK_PRESETS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </label>

        <label className="text-xs">
          <span className="label-xs block mb-1">Min Confidence (50-100)</span>
          <input data-testid="ai-min-confidence" type="number" min="50" max="100" step="1"
                  defaultValue={field("min_confidence")}
                  onBlur={(e) => save({ min_confidence: parseInt(e.target.value, 10) })} className="input-dark" />
        </label>
        <label className="text-xs">
          <span className="label-xs block mb-1">Signal Expiry (min)</span>
          <input data-testid="ai-signal-expiry" type="number" min="1" max="30" step="1"
                  defaultValue={field("signal_expiry_min")}
                  onBlur={(e) => save({ signal_expiry_min: parseInt(e.target.value, 10) })} className="input-dark" />
        </label>
        <label className="text-xs">
          <span className="label-xs block mb-1">Starting Equity (USD)</span>
          <input data-testid="ai-starting-equity" type="number" min="0" step="100"
                  defaultValue={field("starting_equity")}
                  onBlur={(e) => save({ starting_equity: parseFloat(e.target.value) })} className="input-dark" />
        </label>

        <label className="text-xs">
          <span className="label-xs block mb-1">Daily Loss Limit %</span>
          <input data-testid="ai-daily-loss" type="number" min="0.5" max="20" step="0.5"
                  defaultValue={field("daily_loss_limit")}
                  onBlur={(e) => save({ daily_loss_limit: parseFloat(e.target.value) })} className="input-dark" />
        </label>
        <label className="text-xs">
          <span className="label-xs block mb-1">Weekly Loss Limit %</span>
          <input data-testid="ai-weekly-loss" type="number" min="0.5" max="30" step="0.5"
                  defaultValue={field("weekly_loss_limit")}
                  onBlur={(e) => save({ weekly_loss_limit: parseFloat(e.target.value) })} className="input-dark" />
        </label>
        <label className="text-xs">
          <span className="label-xs block mb-1">Max Trades / Day</span>
          <input data-testid="ai-max-trades" type="number" min="1" max="50" step="1"
                  defaultValue={field("max_trades_per_day")}
                  onBlur={(e) => save({ max_trades_per_day: parseInt(e.target.value, 10) })} className="input-dark" />
        </label>
      </div>

      <label className="text-xs flex items-center gap-2 pt-2">
        <input data-testid="ai-news-filter" type="checkbox" checked={!!field("news_filter")}
                onChange={(e) => save({ news_filter: e.target.checked })} />
        Enable ForexFactory news blackout (USD High-Impact ± 30 min; FOMC/NFP ± 45)
      </label>

      <div className="pt-3 border-t border-white/5 space-y-2">
        <p className="label-xs flex items-center gap-2 text-amber-400">
          <Zap size={11} /> Simple Signal Mode (pipeline testing)
        </p>
        <label className="text-xs flex items-center gap-2">
          <input data-testid="ai-simple-mode-toggle" type="checkbox" checked={!!field("simple_mode")}
                  onChange={(e) => save({ simple_mode: e.target.checked })} />
          <span>
            Enable Simple Mode — bypasses ATR/session/news/trend/level/confidence gates.
            Every M1 close: <span className="text-[#00F0FF]">green=BUY</span>, <span className="text-[#FF3B30]">red=SELL</span>.
            Fixed 0.01 lot · SL ±$0.30 buffer · TP 1R · force-close after N min.
          </span>
        </label>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="text-xs">
            <span className="label-xs block mb-1">Simple max hold (min)</span>
            <input data-testid="ai-simple-hold" type="number" min="1" max="120" step="1"
                    defaultValue={field("simple_max_hold_min") || 10}
                    onBlur={(e) => save({ simple_max_hold_min: parseInt(e.target.value, 10) })} className="input-dark" />
          </label>
          <label className="text-xs">
            <span className="label-xs block mb-1">Simple min gap (min)</span>
            <input data-testid="ai-simple-gap" type="number" min="1" max="60" step="1"
                    defaultValue={field("simple_min_gap_min") || 5}
                    onBlur={(e) => save({ simple_min_gap_min: parseInt(e.target.value, 10) })} className="input-dark" />
          </label>
        </div>
      </div>

      <div className="pt-3 border-t border-white/5 space-y-2">
        <p className="label-xs flex items-center gap-2 text-[#7000FF]">
          <Beaker size={11} /> Dev Mode (testing only — bypasses production gates)
        </p>
        <label className="text-xs flex items-center gap-2">
          <input data-testid="ai-dev-mode-toggle" type="checkbox" checked={!!field("dev_mode")}
                  onChange={(e) => save({ dev_mode: e.target.checked })} />
          Enable Dev Mode (relaxes KYC / consent / approval gates for eligibility)
        </label>
        <label className="text-xs flex items-center gap-2">
          <span className="text-gray-400 shrink-0">Test account_id:</span>
          <input data-testid="ai-test-account-id" className="input-dark flex-1"
                  placeholder="Set from Eligibility Debug → Set as Test button"
                  defaultValue={field("test_account_id") || ""}
                  onBlur={(e) => save({ test_account_id: e.target.value || null })} />
        </label>
      </div>
    </div>
  );
}

// -------------------------------------------------
// Journal
// -------------------------------------------------
function StepPill({ tag, ok }) {
  const map = {
    FETCH: "bg-blue-500/15 text-blue-300 border-blue-500/40",
    SIGNAL: "bg-purple-500/15 text-purple-300 border-purple-500/40",
    CLAUDE: "bg-[#00F0FF]/15 text-[#00F0FF] border-[#00F0FF]/40",
    ORDER: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    FILL: "bg-[#00F0FF]/15 text-[#00F0FF] border-[#00F0FF]/40",
    GATE: "bg-white/5 text-gray-300 border-white/10",
    CLOSE: "bg-gray-500/15 text-gray-300 border-gray-500/40",
  };
  const failCls = "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/40";
  const cls = ok ? (map[tag] || map.GATE) : failCls;
  return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls} tabular font-mono`}>[{tag}{ok ? "" : "!"}]</span>;
}

function CycleRow({ c }) {
  const [expanded, setExpanded] = useState(false);
  const steps = c?.risk_diag?.steps || [];
  const passed = (c.filters_passed || []).join(" · ");
  const failed = (c.filters_failed || []).join(" · ");
  return (
    <>
      <tr className="border-b border-white/5 cursor-pointer hover:bg-white/5" onClick={() => setExpanded(!expanded)} data-testid={`cycle-row-${c.cycle_id}`}>
        <Td className="text-[11px] text-gray-500">{new Date(c.ts).toLocaleTimeString()}</Td>
        <Td className="text-[11px] text-[#00F0FF] !whitespace-normal max-w-xs">{passed}</Td>
        <Td className="text-[11px] text-amber-400 !whitespace-normal max-w-md">{failed}</Td>
        <Td className="text-[11px] text-gray-500">
          {c.decision?.signal_id || "—"}
          {steps.length > 0 && <span className="ml-2 text-gray-600">({steps.length} steps · click to expand)</span>}
        </Td>
      </tr>
      {expanded && steps.length > 0 && (
        <tr className="border-b border-white/5 bg-black/40">
          <td colSpan={4} className="px-3 py-2">
            <div className="space-y-1 text-[11px] font-mono">
              {steps.map((s, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-gray-600 shrink-0">{s.ts?.slice(11, 19)}</span>
                  <StepPill tag={s.tag} ok={s.ok} />
                  <span className={s.ok ? "text-gray-200" : "text-[#FF3B30]"}>{s.msg}</span>
                  {s.extras && Object.keys(s.extras).length > 0 && (
                    <span className="text-gray-600 text-[10px] ml-2">
                      {JSON.stringify(s.extras).slice(0, 240)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Journal() {
  const [tab, setTab] = useState("cycles");
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    setRows(null);
    try {
      const path = tab === "cycles" ? "/claude/journal/cycles" : tab === "claude" ? "/claude/journal/claude" : "/claude/journal/executions";
      const { data } = await api.get(path);
      setRows(data);
    } catch (e) { setRows([]); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="card-dark p-5">
      <div className="flex items-center gap-2 mb-4">
        <p className="text-white font-display text-lg">Signal Journal</p>
        <div className="ml-auto flex gap-1">
          {["cycles", "claude", "executions"].map((t) => (
            <button key={t} data-testid={`ai-journal-tab-${t}`} onClick={() => setTab(t)}
                     className={`text-[11px] px-3 py-1 rounded ${tab === t ? "bg-[#7000FF]/20 text-[#00F0FF]" : "text-gray-400 hover:text-white"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>
      {rows === null ? <div className="skeleton h-24" /> : rows.length === 0 ? (
        <p className="text-xs text-gray-500 py-6 text-center">No entries yet — the scheduler writes here every minute.</p>
      ) : tab === "cycles" ? (
        <Table headers={["Time", "Passed", "Failed", "Signal / Steps"]}>
          {rows.slice(0, 40).map((c) => <CycleRow key={c.cycle_id} c={c} />)}
        </Table>
      ) : tab === "claude" ? (
        <Table headers={["Time", "Model", "Score", "In/Out toks", "Cost", "Latency"]}>
          {rows.slice(0, 40).map((c) => (
            <tr key={c.call_id} className="border-b border-white/5">
              <Td className="text-[11px] text-gray-500">{new Date(c.ts).toLocaleTimeString()}</Td>
              <Td className="text-[11px]">{c.model || "—"}</Td>
              <Td className="tabular text-white">{c.claude_score}</Td>
              <Td className="tabular text-[11px]">{c.input_tokens} / {c.output_tokens}</Td>
              <Td className="tabular text-[11px]">${fmt(c.cost_usd, 4)}</Td>
              <Td className="tabular text-[11px]">{c.latency_ms}ms</Td>
            </tr>
          ))}
        </Table>
      ) : (
        <Table headers={["Time", "Client", "Direction", "Lots", "Order/Error", "Status"]}>
          {rows.slice(0, 40).map((e) => (
            <tr key={e.exec_id} className="border-b border-white/5">
              <Td className="text-[11px] text-gray-500">{new Date(e.ts).toLocaleTimeString()}</Td>
              <Td className="text-xs">{e.client_name || e.user_id}</Td>
              <Td className="text-xs">{e.direction}</Td>
              <Td className="tabular text-xs">{e.lots ?? "—"}</Td>
              <Td className="text-[11px] text-gray-400 !whitespace-normal max-w-sm">{e.order_id || e.error || "—"}</Td>
              <Td><StatusBadge status={e.status === "success" ? "paid" : e.status === "failed" ? "overdue" : "pending"} /></Td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

// -------------------------------------------------
// Main Claude Signals page
// -------------------------------------------------
export default function ClaudeSignals() {
  const [status, setStatus] = useState(null);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [killDialog, setKillDialog] = useState(false);
  const [killClose, setKillClose] = useState(false);
  const [runningCycle, setRunningCycle] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: st }, { data: sigs }] = await Promise.all([
        api.get("/claude/status"),
        api.get("/claude/signals?limit=40"),
      ]);
      setStatus(st);
      setSignals(sigs);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Claude status");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const pending = useMemo(() => signals.filter((s) => s.status === "pending"), [signals]);
  const active = useMemo(() => signals.filter((s) => ["executing", "executed"].includes(s.status)), [signals]);
  const history = useMemo(() => signals.filter((s) => !["pending", "executing", "executed"].includes(s.status)), [signals]);

  const runCycleNow = async () => {
    setRunningCycle(true);
    try {
      await api.post("/claude/debug/run-cycle");
      toast.success("Scheduler cycle triggered");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cycle failed");
    } finally { setRunningCycle(false); }
  };

  const doKill = async () => {
    try {
      const { data } = await api.post(`/claude/kill?reason=manual&close_all=${killClose}`);
      toast.success(`AI killed. Closed ${data.closed_positions} positions`);
      setKillDialog(false);
      await load();
    } catch (e) {
      toast.error("Kill failed");
    }
  };

  const doResume = async () => {
    try {
      await api.post("/claude/resume");
      toast.success("AI resumed");
      await load();
    } catch (e) {
      toast.error("Resume failed");
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Claude Signals"
        sub="Institutional M1 scalping — Rule Engine + Claude analysis for XAUUSD."
      >
        <button data-testid="ai-run-cycle" disabled={runningCycle} onClick={runCycleNow}
                className="btn-outline text-xs px-3 py-2 flex items-center gap-2 disabled:opacity-40">
          <RefreshCw size={12} className={runningCycle ? "animate-spin" : ""} /> Run cycle
        </button>
        {status?.settings?.system_status !== "KILLED" && status?.settings?.system_status !== "PAUSED" ? (
          <button data-testid="ai-kill-switch" onClick={() => setKillDialog(true)}
                  className="text-xs px-3 py-2 rounded-lg flex items-center gap-2 bg-[#FF3B30]/15 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/25">
            <ShieldAlert size={12} /> Emergency Kill
          </button>
        ) : (
          <button data-testid="ai-resume" onClick={doResume}
                  className="btn-primary text-xs px-3 py-2 flex items-center gap-2">
            <Play size={12} /> Resume
          </button>
        )}
      </PageHeader>

      <StatusPanel status={status} />

      {loading ? <div className="skeleton h-32" /> : (
        <SettingsPanel settings={status?.settings} onSaved={(s) => setStatus({ ...status, settings: s })} />
      )}

      {/* Dev-only sections — visible only when dev_mode toggle is ON */}
      {status?.settings?.dev_mode && (
        <>
          <EligibilityDebug onSetTestAccount={async (accId, name) => {
            try {
              const { data } = await api.patch("/claude/settings", { test_account_id: accId });
              setStatus({ ...status, settings: data });
              toast.success(`Test account set: ${name}`);
            } catch { toast.error("Failed to set test account"); }
          }} />
          <ExperimentalMode settings={status?.settings} />
          <DirectMode settings={status?.settings} />
        </>
      )}

      {pending.length > 0 && (
        <div>
          <p className="label-xs mb-3 flex items-center gap-2"><Zap size={12} /> Pending approval ({pending.length})</p>
          <div className="grid md:grid-cols-2 gap-3">
            {pending.map((s) => <SignalCard key={s.signal_id} sig={s} onApprove={load} onReject={load} />)}
          </div>
        </div>
      )}

      {active.length > 0 && (
        <div>
          <p className="label-xs mb-3 flex items-center gap-2"><Activity size={12} /> Active ({active.length})</p>
          <div className="grid md:grid-cols-2 gap-3">
            {active.map((s) => <SignalCard key={s.signal_id} sig={s} onApprove={load} onReject={load} />)}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div>
          <p className="label-xs mb-3">History ({history.length})</p>
          <Table headers={["Time", "Dir", "Entry", "Result", "Score", "Status"]} testId="ai-signal-history">
            {history.slice(0, 30).map((s) => (
              <tr key={s.signal_id} className="border-b border-white/5">
                <Td className="text-[11px] text-gray-500">{new Date(s.created_at).toLocaleString()}</Td>
                <Td className={s.direction === "BUY" ? "text-[#00F0FF]" : "text-[#FF3B30]"}>{s.direction}</Td>
                <Td className="tabular">${fmt(s.entry)}</Td>
                <Td className="text-[11px]">{s.close_reason || s.cancel_reason || s.reject_reason || "—"}</Td>
                <Td className="tabular">{s.final_score}</Td>
                <Td><StatusBadge status={s.status === "closed" ? "paid" : s.status === "rejected" || s.status === "expired" || s.status === "cancelled" ? "overdue" : "pending"} /> <span className="text-[10px] text-gray-500 ml-1">{s.status}</span></Td>
              </tr>
            ))}
          </Table>
        </div>
      )}

      <Journal />

      <Dialog open={killDialog} onOpenChange={setKillDialog}>
        <DialogContent className="bg-[#0a0a0a] border border-white/10">
          <DialogHeader>
            <DialogTitle className="text-[#FF3B30] flex items-center gap-2">
              <ShieldAlert size={18} /> Emergency Kill Switch
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-400">
            This immediately KILLS the AI scheduler. New signals will not be generated.
            You will need to manually Resume to restart.
          </p>
          <label className="text-xs flex items-center gap-2">
            <input data-testid="ai-kill-close-all" type="checkbox" checked={killClose} onChange={(e) => setKillClose(e.target.checked)} />
            Also force-close all open AI positions across every client account
          </label>
          <div className="flex gap-2 pt-2">
            <button onClick={() => setKillDialog(false)} className="btn-outline flex-1 py-2 text-xs">Cancel</button>
            <button data-testid="ai-kill-confirm" onClick={doKill} className="flex-1 py-2 text-xs rounded-lg bg-[#FF3B30]/15 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/25">Kill AI Now</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
